import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

ChartJS.register(ArcElement, Tooltip, Legend);

const IciciSipCalculator = ({ isSip, setIsSip }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [expectedReturnRate, setExpectedReturnRate] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  const calculateSIP = () => {
    const monthlyRate = expectedReturnRate / 100 / 12;
    const months = timePeriod * 12;
    const investedAmount = monthlyInvestment * months;
    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);
    const estimatedReturns = futureValue - investedAmount;

    return {
      investedAmount,
      estimatedReturns,
      totalValue: futureValue,
    };
  };

  const { investedAmount, estimatedReturns, totalValue } = calculateSIP();

  const data = {
    labels: ["Invested Amount", "Estimated Returns"],
    datasets: [
      {
        label: "SIP Returns Breakdown",
        data: [investedAmount, estimatedReturns],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
        {
            "question": "What is the ICICI SIP calculator used for?",
            "answer": "It helps investors estimate the maturity value of their SIP investments in ICICI Mutual Funds based on input parameters such as monthly amount, tenure, and expected return rate."
          },
          {
            "question": "Is the ICICI SIP calculator reliable?",
            "answer": "Yes, it provides a reliable approximation using the compound interest formula, though actual returns may vary based on market performance."
          },
          {
            "question": "How do I access the ICICI SIP calculator?",
            "answer": "You can access it on ICICI Prudential Mutual Fund’s official website or financial advisory portals like TheFinmaster."
          },
          {
            "question": "Can I use the calculator for debt funds or hybrid funds too?",
            "answer": "Absolutely. You can calculate SIP projections for any ICICI Mutual Fund category including equity, debt, hybrid, and ELSS."
          },
          {
            "question": "Does the calculator show returns after tax?",
            "answer": "No, the calculator does not factor in capital gains tax or exit load. It displays gross estimates only."
          },
          {
            "question": "What happens if I change the SIP amount mid-way?",
            "answer": "If you modify your SIP mid-way, your total investment and maturity value will change. The calculator does not auto-adjust for mid-course changes."
          },
          {
            "question": "Is there a reverse SIP calculator option?",
            "answer": "Yes, ICICI and TheFinmaster calculators offer a “goal-based” mode that tells you how much you need to invest monthly to reach a desired corpus."
          },
          {
            "question": "How much should I expect to earn with a 12% return for 5 years?",
            "answer": "A ₹3,000 monthly SIP at 12% return for 5 years can yield a maturity value of around ₹2.5 to ₹2.6 lakh approximately."
          },
          {
            "question": "Can I download or print the calculator results?",
            "answer": "Most online calculators offer export or print functionality for reference and financial planning discussions."
          },
          {
            "question": "Are SIPs safe during market volatility?",
            "answer": "SIPs are designed to average out volatility. Over long tenures, they can be less risky than lump-sum investments."
          },
          {
            "question": "Is the ICICI SIP calculator different from other AMC calculators?",
            "answer": "Functionally it’s similar, but branded calculators like ICICI’s may be tied to specific fund products, whereas platforms like TheFinmaster offer multi-AMC flexibility."
          },
          {
            "question": "Can I use the SIP calculator on mobile?",
            "answer": "Yes, most SIP calculators are responsive and mobile-optimized for smartphones and tablets."
          },
          {
            "question": "What is the minimum SIP amount for ICICI Mutual Fund?",
            "answer": "The minimum SIP amount starts from ₹100 for some funds, though most popular schemes have a ₹500 threshold."
          },
          {
            "question": "Can I calculate returns in both monthly and quarterly SIPs?",
            "answer": "Yes. The calculator usually defaults to monthly, but some tools allow frequency changes to weekly, fortnightly, or quarterly."
          },
          {
            "question": "Are there any hidden charges for using the SIP calculator?",
            "answer": "No, using the calculator is completely free and does not require any payment or registration."
          }
        ]
    
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
      <Helmet>
        <title>
        ICICI SIP Calculator – Estimate Mutual Fund Returns Instantly
        </title>
        <meta
          name="description"
          content="Calculate potential returns on your ICICI SIP investments with this easy-to-use SIP calculator. Perfect for goal-based mutual fund planning."
        />
       
        <link
          rel="canonical"
          href="https://thefinmaster.com/financial-tools-calculators/icici-sip-calculator"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 "> ICICI SIP Calculator</span>
          </li>
        </ol>
        
      </nav> */}
          <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      <div className="py-6">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
           <h1 className="text-2xl font-bold mb-4">ICICI SIP Returns Calculator</h1>
         
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Monthly Investment
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={monthlyInvestment}
                    onChange={(e) =>
                      setMonthlyInvestment(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {monthlyInvestment.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Expected Return Rate (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={expectedReturnRate}
                    onChange={(e) =>
                      setExpectedReturnRate(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{expectedReturnRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={expectedReturnRate}
                  onChange={(e) =>
                    setExpectedReturnRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Time Period (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(Number(e.target.value))}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateSIP}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Returns: ₹ {estimatedReturns.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            ICICI Mutual Fund SIP Calculator
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Systematic Investment Plans (SIPs) have become one of the most popular investment methods for individuals looking to grow their wealth in a disciplined and consistent manner. With SIPs, investors contribute a fixed sum at regular intervals—usually monthly—into a mutual fund of their choice. ICICI Mutual Fund offers a wide range of SIP options to suit the needs of conservative, moderate, and aggressive investors alike.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            SIPs offer the benefit of rupee cost averaging and compounding, allowing investors to smooth out market volatility over time. Unlike lump-sum investments, SIPs are more affordable and convenient, helping individuals build a strong corpus without timing the market.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            To assist investors in planning and visualizing their long-term financial goals, ICICI offers the ICICI SIP Calculator—a simple yet powerful online tool that projects the future value of investments based on input parameters such as monthly amount, expected return, and investment period.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is SIP and Why Is It Popular?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A SIP is not a type of mutual fund; instead, it’s a method of investing in mutual funds gradually over time. Rather than making a one-time lump-sum investment, investors can choose to invest smaller amounts at regular intervals — weekly, monthly, or quarterly. This systematic approach brings financial discipline, promotes saving habits, and reduces the burden of market timing. It also leverages the benefits of rupee cost averaging and compounding, making it especially useful for long-term financial goals such as retirement, education, homeownership, or wealth accumulation.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              HDFC, being one of India’s leading financial institutions, offers a wide range of mutual fund schemes that are SIP-enabled. Whether you are investing in an Equity Linked Savings Scheme (ELSS) for tax benefits or in diversified equity funds for long-term growth, SIPs make your investment journey more manageable and methodical. To make this experience even more intuitive, HDFC provides access to a dedicated SIP calculator, which can help investors visualize potential returns and plan their investments more accurately.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Can the ICICI SIP Calculator Help You?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The ICICI SIP calculator empowers investors to make informed decisions before committing to a long-term investment. Here are some key benefits of using the ICICI SIP calculator:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Goal Planning:</strong> Whether you're saving for a home, retirement, or your child’s education, the calculator helps you forecast how much your SIPs can grow over a defined period.</li>
              <li><strong>Instant Projections:</strong> It instantly provides an approximate maturity value based on your investment amount, duration, and expected returns.</li>
              <li><strong>Custom Scenarios:</strong> You can modify variables in real-time to compare different investment strategies and decide the most optimal path.</li>
              <li><strong>Reverse Calculations:</strong> The tool can also tell you how much you should invest monthly to reach a target amount within a specific tenure.</li>
              <li><strong>Financial Discipline:</strong> Seeing the long-term impact of small, consistent investments motivates users to stay committed to their SIPs.</li>
              <li><strong>Risk Evaluation:</strong> By adjusting the expected return rate, users can visualize outcomes for conservative, moderate, and aggressive market scenarios.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the ICICI SIP Calculator Work?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            M = P × [(1 + i)<sup>n</sup> - 1] / i × (1 + i)
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>M = Maturity amount (the total amount you will get at the end)</li>
              <li>P = SIP amount (the amount you invest every month)</li>
              <li>i = Monthly rate of return (annual expected return ÷ 12 ÷ 100)</li>
              <li>n = Total number of SIP installments (investment period in months)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Suppose you invest ₹3,000 per month for 5 years (60 months), and expect a 12% annual return:<br />
              P = 3,000<br />
              i = 12 / 12 / 100 = 0.01 (i.e., 1%)<br />
              n = 60<br />
              Apply the values:<br />
              M = 3,000 × [(1 + 0.01)<sup>60</sup> - 1] / 0.01 × (1 + 0.01)<br />
              M ≈ ₹2,46,507<br />
              So, your ₹1,80,000 investment (3,000 × 60) could grow to approximately ₹2,46,507 over 5 years.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the ICICI SIP Calculator Online?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the ICICI Mutual Fund SIP calculator is straightforward and user-friendly. Here’s how:
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Visit the Calculator Page: Go to the official ICICI Mutual Fund website or a reliable financial portal offering the tool.</li>
              <li>Input Monthly Investment: Enter the amount you intend to invest each month.</li>
              <li>Select Investment Duration: Choose the number of years you want to continue the SIP.</li>
              <li>Enter Expected Return Rate: Input a realistic annual return based on historical performance and market assumptions.</li>
              <li>Get Instant Results: The calculator will show you the total invested amount, estimated returns, and projected maturity value.</li>
            </ol>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example Scenario:</strong><br />
              Suppose you want to invest ₹6,000 per month for 8 years, expecting a 10% annual return.<br />
              Upon input, the calculator shows:<br />
              Total Investment: ₹5,76,000<br />
              Estimated Returns: ₹3,52,867<br />
              Maturity Value: ₹9,28,867
            </p>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Advantages of Using TheFinmaster’s SIP Calculator for ICICI Funds
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Brand-Neutral Insights: Unlike brand-specific tools, TheFinmaster SIP calculator provides independent projections across all fund houses, including ICICI.
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Reverse Goal Calculation:</strong> Plan backwards from a future goal and let the calculator tell you the right SIP amount.</li>
              <li><strong>Cross-Comparison:</strong> Compare ICICI SIP performance with other mutual fund SIPs in one place.</li>
              <li><strong>Data-Driven Design:</strong> Based on historical benchmarks and expected inflation, it delivers more grounded estimates.</li>
              <li><strong>Easy-to-Use Interface:</strong> Even first-time investors can calculate and understand their future wealth in under a minute.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Which Factors Influence SIP Earnings?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              SIP returns are not fixed. Various dynamic elements can impact how your investments grow:
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Monthly Instalment Amount</strong><br />
                The higher your SIP amount, the greater your investment base. Even a small increase in monthly contributions (e.g., ₹500 more) can result in lakhs more at maturity due to compounding.</li>
              <li><strong>Investment Tenure</strong><br />
                Time plays a crucial role. SIPs are ideal for long-term investing. Staying invested for 10+ years helps ride market volatility and benefit from compounding.</li>
              <li><strong>Expected Rate of Return</strong><br />
                This depends on the performance of the mutual fund and market conditions. Equity funds typically offer higher returns but come with more risk, while debt funds are more stable but offer lower growth.</li>
              <li><strong>Market Conditions</strong><br />
                Mutual funds are market-linked. The returns can fluctuate based on macroeconomic trends, interest rates, inflation, and corporate performance. Long-term SIPs help average out these fluctuations.</li>
              <li><strong>Fund Type and Strategy</strong><br />
                Different mutual funds follow varied strategies — large-cap, mid-cap, small-cap, sectoral, hybrid, etc. Your choice of fund will impact both the risk and return potential.</li>
              <li><strong>Taxation</strong><br />
                While ELSS SIPs offer tax benefits under Section 80C, most equity fund gains are taxed at 10% beyond ₹1 lakh of annual gains (LTCG), and short-term gains are taxed at 15%. This affects your post-tax returns.</li>
            </ol>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
              </ul>
            </div>
          </div>
                      {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
             {/* FAQ section */}
    <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
                <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default IciciSipCalculator;
